import type { ResidenceSectionV2 } from "@credit-acceptance/step6-bff-client";

import { EMPTY_RESIDENCE_VERIFIED_FIELDS, type ResidenceVerifiedFields } from "./callScriptResidence.types";

// ---------------------------------------------------------------------------
// callScriptResidence.mapping
//
// Maps the dashboard's ResidenceSectionV2 onto the Customer Verification Call
// Script's "Saved Information" column. Mirrors callScriptEmploymentMapping.ts
// (the employment equivalent) for the fields this section actually carries —
// the Contact block's date of birth/primary/secondary phone, and the
// Physical/Mailing Address blocks.
//
// The Deal/down-payment block (whoIsPrimaryDriver, tradeIn,
// totalCashAndTradeIn, anyAmountStillOwed, whenIsRemainingDue,
// grossTradeInAmount, tradeInPayoffAmount, cashDownPaidOnDelivery,
// amountDueAfterDelivery, whenIsFinalAmountDue) has no BFF/dashboard backing
// anywhere in this app — those fields are left at their empty defaults.
// ---------------------------------------------------------------------------

/**
 * Maps a saved `ResidenceSectionV2` into the modal's field shape, for
 * rendering the read-only "Saved Information" controls (same input types as
 * "Verified Information", disabled). The Deal/down-payment block has no BFF
 * backing and is left at its empty defaults — see the module comment.
 * @param {ResidenceSectionV2 | undefined} residence - The applicant's saved residence section, if any.
 * @returns {ResidenceVerifiedFields} The saved-fields state.
 */
export function mapResidenceToSavedFields(residence: ResidenceSectionV2 | undefined): ResidenceVerifiedFields {
  if (!residence) {
    return { ...EMPTY_RESIDENCE_VERIFIED_FIELDS };
  }
  const physical = residence.current;
  const mailing = residence.mailing;
  const phones = [...(residence.phones ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  return {
    ...EMPTY_RESIDENCE_VERIFIED_FIELDS,
    dateOfBirth: residence.dateOfBirth ?? "",
    primaryNumber: phones[0]?.number ?? "",
    secondaryNumber: phones[1]?.number ?? "",
    physicalStreetNumber: physical?.streetNumber ?? "",
    physicalStreetName: physical?.streetName ?? "",
    physicalApartmentNumber: physical?.apartmentNumber ?? "",
    physicalZip: physical?.zip ?? "",
    physicalCity: physical?.city ?? "",
    physicalState: physical?.state ?? "",
    mailingStreetNumber: mailing?.streetNumber ?? "",
    mailingStreetName: mailing?.streetName ?? "",
    mailingApartmentNumber: mailing?.apartmentNumber ?? "",
    mailingZip: mailing?.zip ?? "",
    mailingCity: mailing?.city ?? "",
    mailingState: mailing?.state ?? "",
  };
}
